import {
  BLACKOUT_CATEGORY,
  BUNDLED_EXPERIENCES_CATEGORY,
  CAMPAIGN_CATEGORY,
  DATES_RESTRICTIONS_CATEGORY,
  DEAL_OPTIONS_INCLUSIONS_CATEGORY,
  EGS_CATEGORY,
  SETUP_CATEGORY,
  SURCHARGES_CATEGORY,
  TAXES_PAYMENTS_CATEGORY,
} from './checklistCategories';
import type { BlackoutChecklistRow } from './tabs/blackoutDates/types';
import type { BundledExperienceChecklistRow } from './tabs/bundledExperiences/types';
import type { CampaignChecklistRow } from './tabs/campaign/types';
import type { DatesRestrictionsChecklistRow } from './tabs/datesRestrictions/types';
import type { DealOptionsInclusionsChecklistRow } from './tabs/dealOptionsInclusions/types';
import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './tabs/egs/types';
import type { SetupChecklistRow } from './tabs/setup/types';
import type { SurchargeTableRow, SurchargesChecklistRow } from './tabs/surcharges/types';
import type { TaxesPaymentsChecklistRow } from './tabs/taxesPayments/types';
import type { ApprovalReportSummary, ChecklistRow, ManualApprovalState } from './types';

export function filterRows<Row>({
  isApproved,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isApproved: (row: Row) => boolean;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAutoApproved && isApproved(row)) {
      return false;
    }

    return !normalizedSearchQuery || text(row).toLowerCase().includes(normalizedSearchQuery);
  });
}

export function buildReportSummaries({
  blackoutRows,
  bundledExperienceRows,
  baseSurchargesRows,
  taxesPaymentsRows,
  capacityRows,
  campaignRows,
  datesRestrictionsRows,
  dealOptionsInclusionsRows,
  manualApprovals,
  marginRows,
  offerId,
  propertyId,
  setupRows,
  surchargeTableRows,
  vendorId,
}: {
  blackoutRows: Array<BlackoutChecklistRow>;
  bundledExperienceRows: Array<BundledExperienceChecklistRow>;
  baseSurchargesRows: Array<SurchargesChecklistRow>;
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  campaignRows: Array<CampaignChecklistRow>;
  datesRestrictionsRows: Array<DatesRestrictionsChecklistRow>;
  dealOptionsInclusionsRows: Array<DealOptionsInclusionsChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  offerId: string;
  propertyId?: string;
  setupRows: Array<SetupChecklistRow>;
  surchargeTableRows: Array<SurchargeTableRow>;
  vendorId?: string;
}): Array<ApprovalReportSummary> {
  const summaries: Array<ApprovalReportSummary> = [];

  const setupRowsRequiringApproval = setupRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (setupRowsRequiringApproval.length > 0) {
    const setupRowCopy = `setup row${setupRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: SETUP_CATEGORY.id,
      categoryLabel: SETUP_CATEGORY.label,
      count: setupRowsRequiringApproval.length,
      description: `${setupRowsRequiringApproval.length} ${setupRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const campaignRowsRequiringApproval = campaignRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (campaignRowsRequiringApproval.length > 0) {
    const campaignRowCopy = `campaign row${campaignRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: CAMPAIGN_CATEGORY.id,
      categoryLabel: CAMPAIGN_CATEGORY.label,
      count: campaignRowsRequiringApproval.length,
      description: `${campaignRowsRequiringApproval.length} ${campaignRowCopy} still need review.`,
      sourceLinks: [
        {
          href: 'https://aussiecommerce.atlassian.net/wiki/spaces/SO/pages/2335834126/Contracting+and+Assessment+Information+Chains+Hotel+Specific',
          label: 'Open Policy Bible',
        },
      ],
    });
  }

  const datesRestrictionsRowsRequiringApproval = datesRestrictionsRows.filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (datesRestrictionsRowsRequiringApproval.length > 0) {
    const datesRestrictionsRowCopy = `Dates & Restrictions row${
      datesRestrictionsRowsRequiringApproval.length === 1 ? '' : 's'
    }`;
    summaries.push({
      categoryId: DATES_RESTRICTIONS_CATEGORY.id,
      categoryLabel: DATES_RESTRICTIONS_CATEGORY.label,
      count: datesRestrictionsRowsRequiringApproval.length,
      description: `${datesRestrictionsRowsRequiringApproval.length} ${datesRestrictionsRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const blackoutRowsRequiringApproval = blackoutRows.filter((row) => !isRowApproved(row, manualApprovals));
  const blackoutReviewCount = blackoutRowsRequiringApproval.length;
  if (blackoutReviewCount > 0) {
    const blackoutRowCopy = `blackout row${blackoutReviewCount === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: BLACKOUT_CATEGORY.id,
      categoryLabel: BLACKOUT_CATEGORY.label,
      count: blackoutReviewCount,
      description: `${blackoutReviewCount} ${blackoutRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const marginRowsRequiringApproval = marginRows.filter((row) => !isRowApproved(row, manualApprovals));
  const capacityRowsRequiringApproval = capacityRows.filter((row) => !isRowApproved(row, manualApprovals));
  const egsRowsRequiringApproval = [...marginRowsRequiringApproval, ...capacityRowsRequiringApproval];
  if (egsRowsRequiringApproval.length > 0) {
    const egsParts = [
      buildReviewCountCopy(marginRowsRequiringApproval.length, 'margin row'),
      buildReviewCountCopy(capacityRowsRequiringApproval.length, 'capacity row'),
    ].filter(Boolean);
    summaries.push({
      categoryId: EGS_CATEGORY.id,
      categoryLabel: EGS_CATEGORY.label,
      count: egsRowsRequiringApproval.length,
      description: `${joinReviewCountParts(egsParts)} still need review.`,
      sourceLinks: buildEgsSourceLinks({ capacityRows, propertyId, vendorId }),
    });
  }

  const dealRowsRequiringApproval = dealOptionsInclusionsRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (dealRowsRequiringApproval.length > 0) {
    const dealRowCopy = `inclusion row${dealRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: DEAL_OPTIONS_INCLUSIONS_CATEGORY.id,
      categoryLabel: DEAL_OPTIONS_INCLUSIONS_CATEGORY.label,
      count: dealRowsRequiringApproval.length,
      description: `${dealRowsRequiringApproval.length} ${dealRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const bundledExperienceRowsRequiringApproval = bundledExperienceRows.filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (bundledExperienceRowsRequiringApproval.length > 0) {
    const bundledExperienceRowCopy = `bundled experience row${
      bundledExperienceRowsRequiringApproval.length === 1 ? '' : 's'
    }`;
    summaries.push({
      categoryId: BUNDLED_EXPERIENCES_CATEGORY.id,
      categoryLabel: BUNDLED_EXPERIENCES_CATEGORY.label,
      count: bundledExperienceRowsRequiringApproval.length,
      description: `${bundledExperienceRowsRequiringApproval.length} ${bundledExperienceRowCopy} still need review.`,
      sourceLinks: [{ href: `/opportunity/${offerId}`, label: 'Verify source data' }],
    });
  }

  const taxesRowsRequiringApproval = taxesPaymentsRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (taxesRowsRequiringApproval.length > 0) {
    const taxesRowCopy = `rule${taxesRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: TAXES_PAYMENTS_CATEGORY.id,
      categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
      count: taxesRowsRequiringApproval.length,
      description: `${taxesRowsRequiringApproval.length} ${taxesRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const surchargeRowsRequiringApproval = [
    ...baseSurchargesRows.filter((row) => !isRowApproved(row, manualApprovals)),
    ...surchargeTableRows.filter((row) => !isRowApproved(row, manualApprovals)),
  ];
  if (surchargeRowsRequiringApproval.length > 0) {
    const surchargeRowCopy = `surcharge row${surchargeRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: SURCHARGES_CATEGORY.id,
      categoryLabel: SURCHARGES_CATEGORY.label,
      count: surchargeRowsRequiringApproval.length,
      description: `${surchargeRowsRequiringApproval.length} ${surchargeRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  return summaries;
}

export function isRowApproved(row: ChecklistRow, manualApprovals: ManualApprovalState) {
  return row.status === 'approved' || !!manualApprovals[row.id];
}

function buildReviewCountCopy(count: number, noun: string) {
  if (count === 0) {
    return '';
  }

  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function joinReviewCountParts(parts: Array<string>) {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function buildEgsSourceLinks({
  capacityRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  propertyId?: string;
  vendorId?: string;
}) {
  if (!propertyId || !vendorId) {
    return [];
  }

  const sourceRow = capacityRows.find((row) => row.roomTypeId && row.roomRateId);

  if (!sourceRow?.roomTypeId || !sourceRow.roomRateId) {
    return [];
  }

  return [
    {
      href: `/vendors/${vendorId}/properties/${propertyId}/room-types/${sourceRow.roomTypeId}/room-rates/${sourceRow.roomRateId}`,
      label: 'Verify source data',
    },
  ];
}
